import { initContract } from "@ts-rest/core";
import { StreamableFile } from "@nestjs/common";
import { z } from "zod";
import { ErrorMessageSchema } from "../schemas/error.schema";
import { billExportColumnSchema } from "../schemas/domains/bills/bill-export.schema";
import { createBillsBodySchema } from "../schemas/domains/bills/create-bill.schema";
import { patchImporterBillsBodySchema } from "../schemas/domains/bills/patch-importer-bills.schema";
import { updateBillLineItemBodySchema } from "../schemas/domains/bills/bill-line-item.schema";
import { updateBillBodySchema } from "../schemas/domains/bills/update-bill.schema";
import { BillScheduleChange } from "../schemas/domains/bills/bill-schedule-change.schema";
import { billDocumentBodySchema } from "../schemas/domains/bills/bill-document.schema";
import { billsPageResponseSchema } from "../schemas/domains/bills/bill-response.schema";
import { accountOverridesRequestSchema } from "../schemas/domains/accounting/account-override.schema";
import {
    billAccrualMatchSummariesQuerySchema,
    billAccrualMatchSummariesResponseSchema,
    documentAccrualMatchesResponseSchema,
} from "../schemas/domains/accrualReview/accrual-review.schema";
import { documentImportedSettlementsResponseSchema } from "../schemas/domains/accrualReview/imported-settlement-display.schema";
import {
    BillBase,
    BillsByCounterpartyItem,
    BillStats,
    BillStatus,
    BillViewOptionGroup,
} from "../types/bill";
import { DocumentMatchStatus } from "../types/matchStatus";

const c = initContract();

const BillWorkspacePathParamsSchema = z.object({
    workspaceId: z.string().uuid(),
});

const BillLineItemPathParamsSchema = z.object({
    workspaceId: z.string().uuid(),
    lineItemId: z.string().uuid(),
});

const BillDocumentPathParamsSchema = z.object({
    workspaceId: z.string().uuid(),
    billId: z.string().uuid(),
});

const BillStatusArraySchema = z.preprocess(
    (value) => (typeof value === "string" ? [value] : value),
    z.array(z.nativeEnum(BillStatus)),
);

export const BillFiltersSchema = z.object({
    ids: z.array(z.string()).optional(),
    search: z.string().optional(),
    entityIds: z.array(z.coerce.number()).optional(),
    billStatuses: BillStatusArraySchema.optional(),
    externalBillStatuses: BillStatusArraySchema.optional(),
    start: z.coerce.date().optional(),
    end: z.coerce.date().optional(),
    startDate: z.string().date().optional(),
    endDate: z.string().date().optional(),
    overdue: z.coerce.boolean().optional(),
    minDue: z.coerce.number().optional(),
    maxDue: z.coerce.number().optional(),
    minAmount: z.coerce.number().optional(),
    maxAmount: z.coerce.number().optional(),
    accountIds: z.array(z.coerce.number()).optional(),
    counterpartiesIds: z.array(z.string()).optional(),
    withEmptyOrUnverifiedCounterparty: z.boolean().optional(),
    hasCounterparty: z.boolean().optional(),
    hasOpenAmount: z.coerce.boolean().optional(),
    matchStatuses: z.array(z.nativeEnum(DocumentMatchStatus)).optional(),
});

export type BillFilters = z.infer<typeof BillFiltersSchema>;

const BillQueryParamsSchema = z.object({
    filters: BillFiltersSchema.optional(),
    sort: z
        .object({
            field: z.string(),
            order: z.enum(["ASC", "DESC"]),
        })
        .optional(),
    page: z.number(),
    limit: z.number(),
});
export type BillQueryParams = z.infer<typeof BillQueryParamsSchema>;

// Each pinned id is one SQL parameter; stay well below Postgres' 65535 cap.
export const MAX_QUERY_BILLS_IDS = 50_000;
export const MAX_QUERY_BILLS_LIMIT = 500;

const QueryBillsBodySchema = BillQueryParamsSchema.extend({
    filters: BillFiltersSchema.extend({
        ids: z.array(z.string().uuid()).max(MAX_QUERY_BILLS_IDS).optional(),
    }).optional(),
    page: z.number().int().min(0),
    limit: z.number().int().min(1).max(MAX_QUERY_BILLS_LIMIT),
});

export const BillStatsParamsSchema = z.object({
    filters: BillFiltersSchema.optional(),
});
export type BillStatsParams = z.infer<typeof BillStatsParamsSchema>;

export const BillsByCounterpartyQueryParamsSchema = z.object({
    filters: BillFiltersSchema.optional(),
    sort: z
        .object({
            field: z.string(),
            order: z.enum(["ASC", "DESC"]),
        })
        .optional(),
    page: z.number(),
    limit: z.number(),
});
export type BillsByCounterpartyQueryParams = z.infer<
    typeof BillsByCounterpartyQueryParamsSchema
>;

export const BillExportQueryParamsSchema = z.object({
    filters: BillFiltersSchema.optional(),
    sort: z
        .object({
            field: z.string(),
            order: z.enum(["ASC", "DESC"]),
        })
        .optional(),
    groupBy: z.nativeEnum(BillViewOptionGroup),
    columns: z.array(billExportColumnSchema).min(1),
});
export type BillExportQueryParams = z.infer<typeof BillExportQueryParamsSchema>;

const BulkUpdateBillLineItemsBodySchema = z
    .object({
        classIds: z.array(z.string().uuid()).optional(),
        accountOverrides: accountOverridesRequestSchema.optional(),
        accountsPayableOverrides: accountOverridesRequestSchema.optional(),
        // Transitional legacy fields. New callers should use the
        // per-ledger override arrays; these resolve to the entity's
        // primary accrual ledger server-side.
        accountId: z.string().uuid().nullable().optional(),
        accountsPayableId: z.string().uuid().nullable().optional(),
        lineItemIds: z.array(z.string().uuid()).optional(),
        filters: BillFiltersSchema.optional(),
    })
    .refine(
        ({
            classIds,
            accountOverrides,
            accountsPayableOverrides,
            accountId,
            accountsPayableId,
        }) =>
            classIds !== undefined ||
            accountOverrides !== undefined ||
            accountsPayableOverrides !== undefined ||
            accountId !== undefined ||
            accountsPayableId !== undefined,
        {
            message:
                "classIds, accountOverrides, accountsPayableOverrides, accountId or accountsPayableId must be provided",
        },
    )
    .refine(
        ({ lineItemIds, filters }) =>
            (lineItemIds?.length ?? 0) > 0 || filters !== undefined,
        {
            message: "lineItemIds or filters must be provided",
        },
    );

export const billContract = c.router(
    {
        createBills: {
            method: "POST",
            path: "/:workspaceId",
            responses: {
                201: c.type<{ data: BillBase[] }>(),
                400: c.type<{ message: string }>(),
                404: c.type<object>(),
            },
            body: createBillsBodySchema,
            pathParams: BillWorkspacePathParamsSchema,
        },
        createBillsMcp: {
            method: "POST",
            path: "/:workspaceId/mcp",
            responses: {
                201: c.type<{ data: BillBase[] }>(),
                400: c.type<{ message: string }>(),
                404: c.type<object>(),
            },
            body: createBillsBodySchema,
            pathParams: BillWorkspacePathParamsSchema,
        },
        patchMcpBills: {
            method: "PUT",
            path: "/:workspaceId/mcp",
            responses: {
                200: c.type<{ data: BillBase[] }>(),
                400: c.type<{ message: string }>(),
                404: c.type<object>(),
                409: c.type<{ message: string }>(),
            },
            body: patchImporterBillsBodySchema,
            pathParams: BillWorkspacePathParamsSchema,
        },
        getBills: {
            method: "GET",
            path: "/:workspaceId",
            responses: {
                200: billsPageResponseSchema,
            },
            query: BillQueryParamsSchema,
        },
        queryBills: {
            method: "POST",
            path: "/:workspaceId/query",
            responses: {
                200: billsPageResponseSchema,
                400: c.type<{ message: string }>(),
            },
            body: QueryBillsBodySchema,
            pathParams: BillWorkspacePathParamsSchema,
        },
        getBillStats: {
            method: "GET",
            path: "/:workspaceId/stats",
            responses: {
                200: c.type<{
                    data: BillStats;
                }>(),
            },
            query: BillStatsParamsSchema,
        },
        getBillsByCounterparty: {
            method: "GET",
            path: "/:workspaceId/by-counterparty",
            responses: {
                200: c.type<{
                    total: number;
                    pageCount: number;
                    data: BillsByCounterpartyItem[];
                }>(),
            },
            query: BillsByCounterpartyQueryParamsSchema,
        },
        exportBillsXlsx: {
            method: "GET",
            path: "/:workspaceId/xlsx",
            pathParams: BillWorkspacePathParamsSchema,
            query: BillExportQueryParamsSchema,
            responses: {
                200: c.type<StreamableFile>(),
                400: ErrorMessageSchema,
                404: ErrorMessageSchema,
            },
        },
        getLastBillNumber: {
            method: "GET",
            path: "/:workspaceId/last-number",
            responses: {
                200: c.type<{ invoiceNumber: string | null }>(),
                404: c.type<object>(),
            },
            pathParams: BillWorkspacePathParamsSchema,
        },
        patchImporterBills: {
            method: "PUT",
            path: "/:workspaceId/importer",
            responses: {
                200: c.type<{
                    data: BillBase[];
                    scheduleChanges: BillScheduleChange[];
                }>(),
                400: c.type<{ message: string }>(),
                404: c.type<object>(),
                409: c.type<{ message: string }>(),
            },
            body: patchImporterBillsBodySchema,
            pathParams: BillWorkspacePathParamsSchema,
        },
        bulkUpdateBillLineItems: {
            method: "PATCH",
            path: "/:workspaceId/line-items/bulk",
            responses: {
                200: c.type<object>(),
                400: c.type<{ message: string }>(),
                404: c.type<object>(),
            },
            body: BulkUpdateBillLineItemsBodySchema,
            pathParams: BillWorkspacePathParamsSchema,
        },
        updateBillLineItem: {
            method: "PATCH",
            path: "/:workspaceId/line-items/:lineItemId",
            responses: {
                200: c.type<object>(),
                400: c.type<{ message: string }>(),
                404: c.type<object>(),
            },
            body: updateBillLineItemBodySchema,
            pathParams: BillLineItemPathParamsSchema,
        },
        updateBill: {
            method: "PATCH",
            path: "/:workspaceId/:billId",
            responses: {
                200: c.type<object>(),
                400: c.type<{ message: string }>(),
                404: c.type<object>(),
                409: c.type<{ message: string }>(),
            },
            body: updateBillBodySchema,
            pathParams: BillDocumentPathParamsSchema,
        },
        linkDocumentToBill: {
            method: "POST",
            path: "/:workspaceId/:billId/documents/link",
            responses: {
                200: c.type<{ data: { financialDocumentIds: number[] } }>(),
                404: c.type<object>(),
            },
            body: billDocumentBodySchema,
            pathParams: BillDocumentPathParamsSchema,
        },
        unlinkDocumentFromBill: {
            method: "POST",
            path: "/:workspaceId/:billId/documents/unlink",
            responses: {
                200: c.type<{ data: { financialDocumentIds: number[] } }>(),
                404: c.type<object>(),
            },
            body: billDocumentBodySchema,
            pathParams: BillDocumentPathParamsSchema,
        },
        deleteBill: {
            method: "DELETE",
            path: "/:workspaceId/:billId",
            responses: {
                200: c.type<object>(),
                400: c.type<{ message: string }>(),
                404: c.type<object>(),
            },
            pathParams: BillDocumentPathParamsSchema,
        },
        getBillAccrualMatchSummaries: {
            method: "GET",
            path: "/:workspaceId/accrual-match-summaries",
            query: billAccrualMatchSummariesQuerySchema,
            responses: {
                200: billAccrualMatchSummariesResponseSchema,
                400: ErrorMessageSchema,
            },
            pathParams: BillWorkspacePathParamsSchema,
        },
        getBillAccrualMatches: {
            method: "GET",
            path: "/:workspaceId/:billId/accrual-matches",
            responses: {
                200: documentAccrualMatchesResponseSchema,
                404: c.type<object>(),
            },
            pathParams: BillDocumentPathParamsSchema,
        },
        getBillImportedSettlements: {
            method: "GET",
            path: "/:workspaceId/:billId/imported-settlements",
            responses: {
                200: documentImportedSettlementsResponseSchema,
                404: c.type<object>(),
            },
            pathParams: BillDocumentPathParamsSchema,
        },
    },
    {
        pathPrefix: "/bills",
    },
);
